import '@realcommercial.com.au/commercial-kit';
import { ReactElement, useEffect, useState, useRef } from 'react';
import HeaderContent from './HeaderContent';
import styled from 'styled-components';
import { paletteHelper, spacingHelper } from '@rea-group/construct-kit-core';
import { getUser, LockeClientId } from '@realcommercial.com.au/user';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

const HeaderWrapper = styled.header.attrs<{ $hideDivider?: boolean }>(
  (props) => ({
    $hideDivider: props.$hideDivider,
  }),
)`
  position: relative;
  background-color: ${paletteHelper('backgroundPrimary')};
  display: flex;
  justify-content: center;
  ${({ $hideDivider }) =>
    $hideDivider ? '' : `box-shadow: inset 0 -0.0625rem 0 #E5E3E8;`}
`;

const HeaderContainer = styled.div<{ $fullWidth: boolean }>`
  padding: ${spacingHelper('small medium')};
  text-align: center;
  position: relative;
  z-index: 99;
  width: 100%;
  ${(props) => !props.$fullWidth && 'max-width: 75rem;'}

  ${({ theme }) => theme.layoutBreakpoints.extraLarge} {
    padding: ${spacingHelper('medium')};
  }
`;

interface OverlayProps {
  $isMenuOpen: boolean;
}

const Overlay = styled.div<OverlayProps>`
  position: absolute;
  height: 100vh;
  top: 100%;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
  opacity: ${(props) => (props.$isMenuOpen ? '1' : '0')};
  pointer-events: ${(props) => (props.$isMenuOpen ? 'auto' : 'none')};
  transition: opacity 0.3s ease-in-out;
  background: rgba(0, 0, 0, 0.7);
`;

type HeaderProps = {
  currentPage: string;
  lockeClientId: LockeClientId;
  fullWidth?: boolean;
  hideDivider?: boolean;
};

export const Header = ({
  currentPage,
  lockeClientId,
  fullWidth = false,
  hideDivider = false,
}: HeaderProps): ReactElement => {
  const [signedInStatus, setSignedInStatus] = useState<boolean | null>(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const toggleMenu = (): void =>
    setMenuOpen((prevState: boolean) => !prevState);
  const overlayRef = useRef(null);

  useEffect(() => {
    const getSignedInStatus = async (): Promise<void> => {
      setSignedInStatus((await getUser(lockeClientId)).isSignedIn);
    };
    getSignedInStatus();
  }, [currentPage, lockeClientId]);

  useEffect(() => {
    const targetElement = overlayRef.current;

    if (isMenuOpen && targetElement) {
      disableBodyScroll(targetElement);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [isMenuOpen]);

  return (
    <HeaderWrapper $hideDivider={hideDivider}>
      <HeaderContainer $fullWidth={fullWidth}>
        <HeaderContent
          currentPage={currentPage}
          signedInStatus={signedInStatus}
          toggleMenu={toggleMenu}
          isMenuOpen={isMenuOpen}
        />
      </HeaderContainer>
      <Overlay
        ref={overlayRef}
        role="presentation"
        $isMenuOpen={isMenuOpen}
        onClick={toggleMenu}
      />
    </HeaderWrapper>
  );
};
